<template>
  <Dialog
    :visible="visible"
    :header="
      isEditMode
        ? $t('workflowApps.publish.editTitle')
        : $t('workflowApps.publish.title')
    "
    :modal="true"
    :closable="true"
    :draggable="false"
    :resizable="false"
    class="workflow-app-publish-dialog"
    :style="{ width: '800px' }"
    @update:visible="$emit('update:visible', $event)"
    @hide="handleCancel"
  >
    <div class="publish-content">
      <!-- 加载状态 -->
      <div v-if="isLoadingApp" class="text-center py-8">
        <ProgressSpinner />
        <p class="mt-4 text-neutral-600">
          {{ $t('workflowApps.publish.checkingStatus') }}
        </p>
      </div>

      <form v-else class="space-y-6" @submit.prevent="handleSubmit">
        <!-- 基本信息 -->
        <div class="form-section">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t('workflowApps.publish.basicInfo') }}
          </h3>

          <!-- 缩略图 -->
          <div class="form-group mb-6">
            <label for="thumbnail" class="block text-sm font-medium mb-2">
              {{ $t('workflowApps.publish.thumbnail') }}
            </label>
            <div class="thumbnail-upload-container">
              <div
                v-if="formData.thumbnail_url"
                class="thumbnail-preview relative"
              >
                <img
                  :src="formData.thumbnail_url"
                  alt="缩略图"
                  class="thumbnail-image"
                />
                <Button
                  type="button"
                  icon="pi pi-times"
                  severity="danger"
                  size="small"
                  class="absolute -top-2 -right-2"
                  @click="removeThumbnail"
                />
              </div>
              <div v-else class="thumbnail-placeholder">
                <i class="pi pi-image text-4xl text-neutral-400 mb-2"></i>
                <p class="text-sm text-neutral-500 mb-3">
                  {{ $t('workflowApps.publish.noThumbnail') }}
                </p>
              </div>

              <!-- 操作按钮 -->
              <div class="thumbnail-actions">
                <Button
                  type="button"
                  icon="pi pi-upload"
                  :label="$t('workflowApps.publish.uploadThumbnail')"
                  severity="secondary"
                  @click="selectThumbnail"
                />
                <Button
                  v-if="formData.thumbnail_url"
                  type="button"
                  icon="pi pi-times"
                  :label="$t('workflowApps.publish.removeThumbnail')"
                  severity="danger"
                  text
                  @click="removeThumbnail"
                />
              </div>
            </div>
          </div>

          <!-- 应用名称 -->
          <div class="form-group">
            <label for="name" class="block text-sm font-medium mb-2">
              {{ $t('workflowApps.publish.appName') }}
              <span class="text-red-500">*</span>
            </label>
            <InputText
              id="name"
              v-model="formData.name"
              :placeholder="$t('workflowApps.publish.appNamePlaceholder')"
              class="w-full"
              :class="{ 'p-invalid': errors.name }"
            />
            <small v-if="errors.name" class="text-red-500">{{
              errors.name
            }}</small>
          </div>
        </div>

        <!-- 应用描述 -->
        <div class="form-group">
          <label for="description" class="block text-sm font-medium mb-2">
            {{ $t('workflowApps.publish.description') }}
          </label>
          <Textarea
            id="description"
            v-model="formData.description"
            :placeholder="$t('workflowApps.publish.descriptionPlaceholder')"
            rows="4"
            class="w-full"
          />
        </div>

        <!-- 工作流数据信息 -->
        <div class="form-section">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t('workflowApps.publish.workflowData') }}
          </h3>
          <div class="workflow-data-card">
            <div class="flex items-start gap-3 mb-4">
              <i class="pi pi-check-circle text-green-400 text-xl mt-0.5"></i>
              <div class="flex-1">
                <span class="text-sm font-medium text-neutral-100">{{
                  $t('workflowApps.publish.loadedFromWorkflow')
                }}</span>
                <p class="text-xs text-neutral-400 mt-1">
                  {{ $t('workflowApps.publish.workflowDataDescription') }}
                </p>
              </div>
            </div>

            <!-- 工作流统计信息 -->
            <div class="grid grid-cols-3 gap-3">
              <div class="workflow-stat-item">
                <div class="flex items-center gap-2">
                  <i class="pi pi-sitemap text-blue-400"></i>
                  <span class="text-xs text-neutral-400">{{
                    $t('workflowApps.publish.nodes')
                  }}</span>
                </div>
                <div class="text-lg font-semibold text-neutral-100 mt-1">
                  {{ workflowStats.nodeCount }}
                </div>
              </div>

              <div class="workflow-stat-item">
                <div class="flex items-center gap-2">
                  <i class="pi pi-link text-purple-400"></i>
                  <span class="text-xs text-neutral-400">{{
                    $t('workflowApps.publish.links')
                  }}</span>
                </div>
                <div class="text-lg font-semibold text-neutral-100 mt-1">
                  {{ workflowStats.linkCount }}
                </div>
              </div>

              <div class="workflow-stat-item">
                <div class="flex items-center gap-2">
                  <i class="pi pi-th-large text-orange-400"></i>
                  <span class="text-xs text-neutral-400">{{
                    $t('workflowApps.publish.groups')
                  }}</span>
                </div>
                <div class="text-lg font-semibold text-neutral-100 mt-1">
                  {{ workflowStats.groupCount }}
                </div>
              </div>
            </div>

            <!-- 输出类型 -->
            <div class="mt-3 pt-3 border-t border-neutral-700">
              <label class="block text-sm font-medium text-neutral-300 mb-2">
                {{ $t('workflowApps.publish.outputType') }}
              </label>
              <Select
                v-model="formData.output"
                :options="outputTypeOptions"
                option-label="label"
                option-value="value"
                class="w-full max-w-xs"
              />
              <small class="block mt-1 text-neutral-500">
                {{ $t('workflowApps.publish.outputTypeHelp') }}
              </small>
            </div>

            <!-- 输入参数信息 -->
            <div
              v-if="workflowStats.inputCount > 0"
              class="mt-3 pt-3 border-t border-neutral-700"
            >
              <div class="flex items-center gap-2 text-xs text-neutral-400">
                <i class="pi pi-sliders-h text-green-400"></i>
                <span
                  >{{ $t('workflowApps.publish.configuredInputs') }}:
                  <strong class="text-neutral-200">{{
                    workflowStats.inputCount
                  }}</strong></span
                >
              </div>

              <!-- 已配置输入参数列表 -->
              <div class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div
                  v-for="input in configuredInputs"
                  :key="input.id"
                  class="rounded-md border border-neutral-700 bg-neutral-900/50 p-2"
                >
                  <div class="flex items-center justify-between">
                    <div class="text-sm font-medium text-neutral-100 truncate">
                      {{ input.name || input.id }}
                    </div>
                    <span
                      class="ml-2 shrink-0 rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300"
                    >
                      {{ input.type || 'text' }}
                    </span>
                  </div>
                  <div
                    v-if="input.placeholder"
                    class="mt-1 text-xs text-neutral-400 truncate"
                  >
                    {{ input.placeholder }}
                  </div>
                  <div
                    v-if="input.value"
                    class="mt-1 text-xs text-neutral-500 truncate"
                  >
                    {{ input.value }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 标签和设置 -->
        <div class="form-section">
          <h3 class="text-lg font-semibold mb-4">
            {{ $t('workflowApps.publish.tagsAndSettings') }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="form-group">
              <label for="tags" class="block text-sm font-medium mb-2">
                {{ $t('workflowApps.publish.tags') }}
              </label>
              <AutoComplete
                v-model="formData.tags"
                :suggestions="tagSuggestions"
                :placeholder="$t('workflowApps.publish.tagsPlaceholder')"
                multiple
                class="w-full"
                @complete="searchTags"
              />
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium mb-2">
                {{ $t('workflowApps.publish.visibility') }}
              </label>
              <div class="flex items-center space-x-4">
                <ToggleSwitch
                  v-model="formData.is_public"
                  :true-label="$t('workflowApps.publish.public')"
                  :false-label="$t('workflowApps.publish.private')"
                />
                <span class="text-sm text-surface-600">
                  {{
                    formData.is_public
                      ? $t('workflowApps.publish.publicDescription')
                      : $t('workflowApps.publish.privateDescription')
                  }}
                </span>
              </div>
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium mb-2">
                {{ $t('workflowApps.publish.appGroup') }}
              </label>
              <Select
                v-model="formData.group_id"
                :options="appGroupOptions"
                option-label="label"
                option-value="value"
                :placeholder="$t('workflowApps.publish.appGroupPlaceholder')"
                class="w-full"
                :loading="loadingGroups"
                show-clear
                style="width: 100%"
              />
            </div>

            <div class="form-group">
              <label class="block text-sm font-medium mb-2">
                {{ $t('workflowApps.publish.requiredCapabilities') }}
              </label>
              <AutoComplete
                v-model="formData.required_capabilities"
                :suggestions="capabilitySuggestions"
                multiple
                :typeahead="false"
                :placeholder="
                  $t('workflowApps.publish.requiredCapabilitiesPlaceholder')
                "
                class="w-full"
                style="width: 100%"
                @complete="searchCapabilities"
                @blur="handleRequiredCapabilitiesBlur"
              />
              <small class="block mt-1 text-surface-500">{{
                $t('workflowApps.publish.requiredCapabilitiesHelp')
              }}</small>
            </div>
          </div>
        </div>

        <!-- 提交按钮 -->
        <div class="form-actions">
          <div class="flex justify-end space-x-4">
            <Button
              type="button"
              :label="$t('g.cancel')"
              severity="secondary"
              @click="handleCancel"
            />
            <Button
              type="submit"
              :label="
                isEditMode
                  ? $t('workflowApps.publish.update')
                  : $t('workflowApps.publish.publish')
              "
              :loading="isSubmitting"
              :disabled="!isFormValid"
            />
          </div>
        </div>
      </form>
    </div>

    <!-- 图片选择对话框 -->
    <ImageSelectorDialog
      v-model:visible="showImageSelector"
      @select="handleImageSelect"
    />
  </Dialog>
</template>

<script setup lang="ts">
import AutoComplete from 'primevue/autocomplete'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import ImageSelectorDialog from '@/components/common/ImageSelectorDialog.vue'
import { useToast } from '@/composables/useToast'
import { useWorkflowStore } from '@/platform/workflow/management/stores/workflowStore'
import { api } from '@/scripts/api'
import { app } from '@/scripts/app'
import {
  type WorkflowAppHistoryGroup,
  useWorkflowAppGroupService
} from '@/services/workflowAppGroupService'

const { t } = useI18n()
const groupService = useWorkflowAppGroupService()
const { showSuccess, showError } = useToast()
const workflowStore = useWorkflowStore()

// 生成工作流API数据
const generateWorkflowApi = async () => {
  try {
    // 使用前端的graphToPrompt方法生成API格式的prompt
    const result = await app.graphToPrompt()

    // 返回API格式的数据
    return {
      client_id: '',
      prompt: result.output || {}
    }
  } catch (error) {
    console.error('生成工作流API失败:', error)
    // 如果生成失败，返回空对象
    return {
      client_id: '',
      prompt: {}
    }
  }
}

// Props
const props = defineProps<{
  visible: boolean
}>()

// Emits
const emit = defineEmits<{
  'update:visible': [value: boolean]
  success: [shareId: string]
}>()

// 表单数据
const formData = ref({
  name: '',
  description: '',
  thumbnail_url: '',
  tags: [] as string[],
  is_public: true,
  group_id: null as number | null,
  required_capabilities: [] as string[],
  output: 'image' as 'image' | 'video'
})

// 服务器能力建议
const capabilitySuggestions = ref<string[]>([])
const handleRequiredCapabilitiesBlur = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target?.value?.trim()
  if (value) {
    const current = formData.value.required_capabilities ?? []
    if (!current.includes(value)) {
      formData.value.required_capabilities = [...current, value]
    }
    target.value = ''
  }
}
const searchCapabilities = (event: { query: string }) => {
  const query = event.query.toLowerCase()
  const singleCapabilities = ['8G', '12G', '16G', '24G', '32G', '48G', '64G']
  const rangeCapabilities = ['12G/24G', '24G/48G', '16G/32G', '32G/64G']
  const allCapabilities = [...singleCapabilities, ...rangeCapabilities]
  capabilitySuggestions.value = allCapabilities.filter((cap) =>
    cap.toLowerCase().includes(query)
  )
}

// 输出类型选项
const outputTypeOptions = [
  { label: t('workflowApps.publish.outputTypeImage'), value: 'image' },
  { label: t('workflowApps.publish.outputTypeVideo'), value: 'video' }
]

// 应用分组
const appGroups = ref<WorkflowAppHistoryGroup[]>([])
const loadingGroups = ref(false)
const appGroupOptions = computed(() => {
  const options: { label: string; value: number | null }[] = [
    { label: t('workflowApps.publish.appGroupEmpty'), value: null }
  ]
  appGroups.value.forEach((group) => {
    options.push({ label: group.name, value: group.id })
  })
  return options
})

// 错误信息
const errors = ref<Record<string, string>>({})

// 提交状态
const isSubmitting = ref(false)

// 图片选择器
const showImageSelector = ref(false)

// 标签建议
const tagSuggestions = ref<string[]>([])

// 发布状态
const isEditMode = ref(false)
const existingAppId = ref<number | null>(null)
const isLoadingApp = ref(false)

// 当前工作流数据 - 优先使用activeState，包含最新的workflowApp配置
const currentWorkflowData = computed(() => {
  const workflow = workflowStore.activeWorkflow
  if (!workflow) return {}

  try {
    // 优先使用activeState，它包含最新的workflowApp配置
    if (workflow.activeState) {
      return JSON.parse(JSON.stringify(workflow.activeState))
    }

    // 如果没有activeState，使用content字段
    const content = workflow.content
    if (content) {
      // 解析content字段（它是JSON字符串）
      return JSON.parse(content)
    }

    // 最后尝试从workflow对象中提取
    return JSON.parse(
      JSON.stringify(workflow, (key, value) => {
        // 跳过可能包含循环引用的属性
        if (key === 'changeTracker' || key === 'workflow') {
          return undefined
        }
        return value
      })
    )
  } catch (error) {
    console.warn('无法序列化工作流数据:', error)
    // 如果序列化失败，返回一个基本的工作流结构
    return {
      nodes: (workflow as any).nodes || {},
      links: (workflow as any).links || [],
      groups: (workflow as any).groups || [],
      config: (workflow as any).config || {},
      extra: (workflow as any).extra || {}
    }
  }
})

// 工作流统计信息
const workflowStats = computed(() => {
  const data = currentWorkflowData.value
  const nodeCount = data.nodes
    ? Array.isArray(data.nodes)
      ? data.nodes.length
      : Object.keys(data.nodes).length
    : 0
  const linkCount = Array.isArray(data.links) ? data.links.length : 0
  const groupCount = Array.isArray(data.groups) ? data.groups.length : 0
  const inputCount = data.extra?.workflowApp?.inputs
    ? Array.isArray(data.extra.workflowApp.inputs)
      ? data.extra.workflowApp.inputs.length
      : Object.keys(data.extra.workflowApp.inputs).length
    : 0

  return {
    nodeCount,
    linkCount,
    groupCount,
    inputCount
  }
})

// 已配置的输入参数明细
const configuredInputs = computed(() => {
  const data = currentWorkflowData.value
  const inputs = data?.extra?.workflowApp?.inputs
  console.log('[WorkflowAppPublishDialog] configuredInputs - 读取数据:', {
    hasData: !!data,
    hasExtra: !!data?.extra,
    hasWorkflowApp: !!data?.extra?.workflowApp,
    hasInputs: !!inputs,
    inputsType: Array.isArray(inputs) ? 'array' : typeof inputs,
    inputsCount: Array.isArray(inputs)
      ? inputs.length
      : Object.keys(inputs || {}).length,
    sampleInput:
      inputs && Array.isArray(inputs) && inputs.length > 0
        ? {
            inputId: inputs[0].inputId || inputs[0].id,
            nodeId: inputs[0].nodeId,
            widgetName: inputs[0].widgetName
          }
        : null
  })

  if (!inputs) return []

  if (Array.isArray(inputs)) {
    const result = inputs.map((i: any) => ({
      id: i.inputId || i.id,
      name: i.name,
      type: i.type,
      value: i.value ?? i.default,
      placeholder: i.placeholder,
      options: i.options,
      config: i.config,
      nodeId: i.nodeId,
      widgetName: i.widgetName
    }))
    console.log(
      '[WorkflowAppPublishDialog] configuredInputs - 处理后的结果:',
      result.map((r: any) => ({
        id: r.id,
        nodeId: r.nodeId,
        widgetName: r.widgetName
      }))
    )
    return result
  }

  // 对象格式（向后兼容）
  const result = Object.entries(inputs).map(([id, i]: [string, any]) => ({
    id,
    name: i.name,
    type: i.type,
    value: i.value ?? i.default,
    placeholder: i.placeholder,
    options: i.options,
    config: i.config,
    nodeId: i.nodeId,
    widgetName: i.widgetName
  }))
  console.log(
    '[WorkflowAppPublishDialog] configuredInputs - 处理后的结果（对象格式):',
    result.map((r: any) => ({
      id: r.id,
      nodeId: r.nodeId,
      widgetName: r.widgetName
    }))
  )
  return result
})

// 表单验证
const isFormValid = computed(() => {
  return (
    formData.value.name.trim() &&
    Object.keys(currentWorkflowData.value).length > 0 &&
    Object.keys(errors.value).length === 0
  )
})

// 验证表单
const validateForm = () => {
  errors.value = {}

  if (!formData.value.name.trim()) {
    errors.value.name = t('workflowApps.publish.errors.nameRequired')
  }

  if (Object.keys(currentWorkflowData.value).length === 0) {
    errors.value.workflow_data = t(
      'workflowApps.publish.errors.workflowDataRequired'
    )
  }

  return Object.keys(errors.value).length === 0
}

// 选择缩略图
const selectThumbnail = () => {
  showImageSelector.value = true
}

// 处理图片选择
const handleImageSelect = (imageUrl: string) => {
  // 交给后端按 STORAGE_TYPE（obs/local/s3）统一解析/规范化，前端不再拼接固定 OBS 前缀
  formData.value.thumbnail_url = imageUrl
  showImageSelector.value = false
}

// 移除缩略图
const removeThumbnail = () => {
  formData.value.thumbnail_url = ''
}

// 搜索标签
const searchTags = () => {
  // 这里可以调用API获取标签建议
  tagSuggestions.value = ['AI', '图像生成', '工作流', '自动化', '创意']
}

// 检查工作流是否已发布
const checkWorkflowPublished = async () => {
  const workflow = workflowStore.activeWorkflow

  // 优先使用 share_id 检查（从工作流数据的 extra 中提取）
  const data = currentWorkflowData.value as any
  const shareId =
    data?.extra?.share_id ||
    data?.extra_data?.extra_pnginfo?.workflow?.extra?.share_id

  // 前置条件：shareId 或 workflowId 任一存在即可
  if (!shareId && !workflow?.workflowId) {
    return
  }

  try {
    isLoadingApp.value = true

    const workflowId = workflow?.workflowId
    const detailUrl = shareId
      ? `/workflow-apps/detail?share_id=${shareId}`
      : `/workflow-apps/detail?workflows_id=${workflowId}`

    const response = await api.fetchApi(detailUrl)

    if (response.ok) {
      const appData = await response.json()
      if (appData) {
        // 工作流已发布，切换到编辑模式
        isEditMode.value = true
        existingAppId.value = appData.id

        // 填充表单数据（required_capabilities 需为新数组引用以触发 AutoComplete 更新）
        const caps = Array.isArray(appData.required_capabilities)
          ? [...appData.required_capabilities]
          : []
        const wf = appData.workflow_data as Record<string, unknown> | undefined
        const extra = wf?.extra as Record<string, unknown> | undefined
        const nested = (wf?.extra_data as Record<string, unknown>)
          ?.extra_pnginfo as Record<string, unknown> | undefined
        const nestedWorkflow = nested?.workflow as
          | Record<string, unknown>
          | undefined
        const nestedExtra = nestedWorkflow?.extra as
          | Record<string, unknown>
          | undefined
        const workflowAppFromApi = (extra?.workflowApp ??
          nestedExtra?.workflowApp) as
          | { output?: 'image' | 'video' }
          | undefined
        formData.value = {
          name: appData.name || '',
          description: appData.description || '',
          thumbnail_url: appData.thumbnail_url || '',
          tags: appData.tags || [],
          is_public: appData.is_public ?? true,
          group_id: appData.group_id ?? null,
          required_capabilities: caps,
          output: workflowAppFromApi?.output === 'video' ? 'video' : 'image'
        }
      }
    }
  } catch (error) {
    console.log('工作流未发布或检查失败:', error)
    // 工作流未发布，保持发布模式
    isEditMode.value = false
    existingAppId.value = null
  } finally {
    isLoadingApp.value = false
  }
}

// 提交表单
const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true

  try {
    // 生成工作流API数据
    const workflowApi = await generateWorkflowApi()

    // 准备工作流数据，添加workflowApi和workflows_id
    const workflowData = { ...currentWorkflowData.value }
    if (!workflowData.extra) {
      workflowData.extra = {}
    }
    workflowData.extra.workflowApi = workflowApi
    workflowData.extra.workflows_id = workflowStore.activeWorkflow?.workflowId

    console.log(
      '[WorkflowAppPublishDialog] handleSubmit - 保存前的工作流数据:',
      {
        workflowAppInputs: workflowData.extra.workflowApp?.inputs,
        workflowAppInputsCount: workflowData.extra.workflowApp?.inputs?.length,
        activeWorkflow: workflowStore.activeWorkflow ? '存在' : '不存在'
      }
    )

    // 确保 workflowApp 存在
    if (!workflowData.extra.workflowApp) {
      workflowData.extra.workflowApp = { inputs: [] }
    }
    ;(workflowData.extra.workflowApp as Record<string, unknown>).output =
      formData.value.output

    // 确保 workflowApp.inputs 包含 nodeId 和 widgetName
    if (workflowData.extra.workflowApp.inputs) {
      const inputs = workflowData.extra.workflowApp.inputs
      if (Array.isArray(inputs)) {
        console.log(
          '[WorkflowAppPublishDialog] handleSubmit - 处理 inputs，数量:',
          inputs.length
        )
        console.log(
          '[WorkflowAppPublishDialog] handleSubmit - 处理前 inputs:',
          JSON.parse(
            JSON.stringify(
              inputs.map((i: any) => ({
                inputId: i.inputId || i.id,
                nodeId: i.nodeId,
                widgetName: i.widgetName
              }))
            )
          )
        )

        // 更新每个输入的 nodeId 和 widgetName（如果 inputId 格式是 {nodeId}-{widgetName}）
        inputs.forEach((input: any, index: number) => {
          const inputId = input.inputId || input.id
          console.log(
            `[WorkflowAppPublishDialog] handleSubmit - 处理输入 ${index}:`,
            {
              inputId,
              nodeId: input.nodeId,
              widgetName: input.widgetName,
              before: { nodeId: input.nodeId, widgetName: input.widgetName }
            }
          )

          if (inputId && typeof inputId === 'string') {
            const parts = inputId.split('-')
            if (parts.length >= 2) {
              // 解析 nodeId 和 widgetName
              input.nodeId = parts.slice(0, -1).join('-')
              input.widgetName = parts[parts.length - 1]
            } else if (parts.length === 1) {
              // 如果只有一个部分，假设整个是 nodeId
              input.nodeId = parts[0]
              input.widgetName = ''
            }

            console.log(
              `[WorkflowAppPublishDialog] handleSubmit - 输入 ${index} 更新后:`,
              {
                inputId,
                nodeId: input.nodeId,
                widgetName: input.widgetName
              }
            )
          }
        })

        console.log(
          '[WorkflowAppPublishDialog] handleSubmit - 所有 inputs 更新后:',
          JSON.parse(
            JSON.stringify(
              inputs.map((i: any) => ({
                inputId: i.inputId || i.id,
                nodeId: i.nodeId,
                widgetName: i.widgetName
              }))
            )
          )
        )

        // 同步更新到工作流存储中
        const activeWorkflow = workflowStore.activeWorkflow
        if (activeWorkflow && activeWorkflow.activeState) {
          if (!activeWorkflow.activeState.extra) {
            activeWorkflow.activeState.extra = {}
          }
          const workflowApp = (activeWorkflow.activeState.extra.workflowApp ??=
            { inputs: [] }) as
            | { inputs: unknown; output?: 'image' | 'video' }
            | Record<string, unknown>
          workflowApp.inputs = JSON.parse(JSON.stringify(inputs))
          workflowApp.output = formData.value.output
          console.log(
            '[WorkflowAppPublishDialog] handleSubmit - 已同步到工作流存储'
          )
        }
      }
    } else {
      console.warn(
        '[WorkflowAppPublishDialog] handleSubmit - workflowApp.inputs 不存在'
      )
    }

    let response: Response

    if (isEditMode.value && existingAppId.value) {
      // 编辑模式：更新现有应用
      response = await api.fetchApi(
        `/workflow-apps/update?app_id=${existingAppId.value}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData.value,
            workflow_data: workflowData,
            workflows_id: workflowStore.activeWorkflow?.workflowId,
            group_id: formData.value.group_id ?? undefined,
            required_capabilities: formData.value.required_capabilities ?? []
          })
        }
      )
    } else {
      // 发布模式：创建新应用
      response = await api.fetchApi('/workflow-apps/publish', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData.value,
          workflow_data: workflowData,
          workflows_id: workflowStore.activeWorkflow?.workflowId,
          group_id: formData.value.group_id ?? undefined,
          required_capabilities: formData.value.required_capabilities ?? []
        })
      })
    }

    if (response.ok) {
      const result = await response.json()
      const successMessage = isEditMode.value
        ? t('workflowApps.publish.updateSuccess')
        : t('workflowApps.publish.publishSuccess')
      showSuccess(successMessage)
      emit('success', result.share_id)
      handleCancel()
    } else {
      const error = await response.json()
      showError(error.detail || t('workflowApps.publish.publishFailed'))
    }
  } catch (error) {
    console.error('发布失败:', error)
    showError(t('workflowApps.publish.publishFailed'))
  } finally {
    isSubmitting.value = false
  }
}

// 取消发布
const handleCancel = () => {
  emit('update:visible', false)
  // 重置表单
  formData.value = {
    name: '',
    description: '',
    thumbnail_url: '',
    tags: [],
    is_public: true,
    group_id: null,
    required_capabilities: [],
    output: 'image'
  }
  errors.value = {}
  isEditMode.value = false
  existingAppId.value = null
}

// 加载应用分组列表
const loadAppGroups = async () => {
  try {
    loadingGroups.value = true
    appGroups.value = await groupService.getGroups(true)
  } catch (error) {
    console.debug('加载应用分组列表失败:', error)
  } finally {
    loadingGroups.value = false
  }
}

// 监听弹窗显示状态
watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      // 从当前工作流初始化 output（新发布时或编辑时作为默认值）
      const data = currentWorkflowData.value as Record<string, unknown>
      const wapp = (data?.extra as Record<string, unknown>)?.workflowApp as
        | { output?: 'image' | 'video' }
        | undefined
      if (wapp?.output === 'video') {
        formData.value.output = 'video'
      }

      // 弹窗打开时，加载应用分组列表
      void loadAppGroups()
      // 弹窗打开时，检查工作流是否已发布
      void checkWorkflowPublished()

      // 检查当前工作流数据
      if (Object.keys(currentWorkflowData.value).length === 0) {
        showError(t('workflowApps.publish.noActiveWorkflow'))
      }
    }
  }
)
</script>

<style scoped>
.workflow-app-publish-dialog :deep(.p-dialog-content) {
  padding: 1.5rem;
}

.form-section {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.thumbnail-upload-container {
  background-color: #2a2a2a;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #404040;
}

.thumbnail-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 1rem;
}

.thumbnail-image {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #404040;
}

.thumbnail-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 200px;
  border: 2px dashed #555;
  border-radius: 8px;
  background-color: #1a1a1a;
  margin-bottom: 1rem;
}

.thumbnail-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.workflow-data-card {
  background-color: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
  padding: 1.25rem;
}

.workflow-stat-item {
  background-color: #0f0f0f;
  border: 1px solid #262626;
  border-radius: 8px;
  padding: 0.875rem;
  transition: all 0.2s ease;
}

.workflow-stat-item:hover {
  border-color: #404040;
  background-color: #1a1a1a;
}

.form-actions {
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid var(--p-border-color);
}
</style>
